import React, { useEffect, useRef, useState } from "react";
import { useToast } from "../context/ToastContext";
import "./ToolPage.css";
import flaskApi from "../services/flaskApi";
import { extractFilenameFromHeader } from "../utils/fileUtils";
import { extractErrorMessage } from "../utils/errorUtils";
import ToolInfoModal from "../components/common/ToolInfoModal";
import ToolFileUploadField from "./shared/ToolFileUploadField";
import ToolPageLayout from "./shared/ToolPageLayout";
import ToolProgressPanel from "./shared/ToolProgressPanel";
import ToolJobsSection from "./shared/ToolJobsSection";
import JobInputActions from "./shared/JobInputActions";
import useJobsPagination from "./shared/useJobsPagination";


const STATUS_SCHEDULED = "scheduled";
const STATUS_DONE = "done";
const STATUS_FAILED = "failed";
const STATUS_CANCELLED = "cancelled";

const ACTION_ADD = "add";
const ACTION_REMOVE = "remove";

const LOOKUP_IDENTIFIER = "identifier";
const LOOKUP_TITLE = "title";

const MATCH_EXACT = "exact";
const MATCH_PREFIX = "prefix";

const RUN_NOW = "now";
const RUN_SCHEDULE = "schedule";

// "YYYY-MM-DDTHH:mm" in the user's local time — what <input type="datetime-local"> expects.
const formatLocalDatetimeInput = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
};

const formatScheduledDisplay = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

// Identifier-column choices the backend understands. Empty = auto-cascade
// (SKU → Shopify Product ID → Handle). 'title' is folded in here so the UI
// has a single picker instead of a 2-radio mode + 4-option picker.
const ID_COL_AUTO = "";
const ID_COL_SKU = "sku";
const ID_COL_PRODUCT_ID = "shopify_product_id";
const ID_COL_HANDLE = "handle";
const ID_COL_TITLE = "title";

const ID_COL_OPTIONS = [
  { value: ID_COL_AUTO, label: "Auto (SKU → ID → Handle)" },
  { value: ID_COL_SKU, label: "SKU column" },
  { value: ID_COL_PRODUCT_ID, label: "Shopify Product ID column" },
  { value: ID_COL_HANDLE, label: "Handle column" },
  { value: ID_COL_TITLE, label: "Title column" },
];

const ID_COL_HELPER = {
  [ID_COL_AUTO]:
    "Tries SKU first, then Shopify Product ID, then Handle. Pick a specific column if your file has overlapping identifiers and one is wrong.",
  [ID_COL_SKU]:
    "Uses only the SKU column (accepts headers 'SKU', 'Variant SKU', or 'Parent SKU'). Matches the parent SKU on Shopify.",
  [ID_COL_PRODUCT_ID]:
    "Uses only the Shopify Product ID column (numeric or full GID). Misses won't fall back to other columns.",
  [ID_COL_HANDLE]:
    "Uses only the Handle (URL slug) column. Useful when SKUs in the file don't match Shopify yet.",
  [ID_COL_TITLE]:
    "Looks up by product title (case-insensitive). Less precise than identifier lookup — use when no SKU/ID/Handle is available.",
};

const ID_COL_LABEL = ID_COL_OPTIONS.reduce((acc, opt) => {
  acc[opt.value] = opt.label;
  return acc;
}, {});

const lookupModeFor = (idCol) =>
  idCol === ID_COL_TITLE ? LOOKUP_TITLE : LOOKUP_IDENTIFIER;

const matchHelperText = (action, matchMode) => {
  if (matchMode === MATCH_EXACT) {
    return action === ACTION_REMOVE
      ? "Removes only the exact tag value (case-insensitive)."
      : "Adds the tag unless that exact value is already present (case-insensitive).";
  }
  return action === ACTION_REMOVE
    ? "Removes every tag on the product whose name starts with the value (case-insensitive). E.g. 'on_map_' removes 'on_map_2026-05-21', 'on_map_2026-04-15', etc."
    : "Skips the add when the product already has any tag starting with your skip prefix. E.g. skip prefix 'offmap_' won't add 'offmap_2026-05-21' if the product already has any 'offmap_…' tag.";
};

const MATCH_LABEL = {
  [MATCH_EXACT]: "Exact",
  [MATCH_PREFIX]: "Prefix",
};

const ENDPOINT_MATRIX = {
  [ACTION_ADD]: {
    [LOOKUP_IDENTIFIER]: "/shopify/add-product-tag",
    [LOOKUP_TITLE]: "/shopify/add-product-tag-by-title",
  },
  [ACTION_REMOVE]: {
    [LOOKUP_IDENTIFIER]: "/shopify/remove-product-tag",
    [LOOKUP_TITLE]: "/shopify/remove-product-tag-by-title",
  },
};

const ACTION_HELPER_TEXT = {
  [ACTION_ADD]: "Skipped if the tag is already on the product.",
  [ACTION_REMOVE]: "Skipped if the tag is not on the product.",
};

const LOOKUP_LABEL = {
  [LOOKUP_IDENTIFIER]: "Identifier",
  [LOOKUP_TITLE]: "Title",
};

function TagUpdateTool() {
  const [inputFile, setInputFile] = useState(null);
  const [tag, setTag] = useState("");
  const [sheetName, setSheetName] = useState("");
  const [action, setAction] = useState(ACTION_ADD);
  const [identifierColumn, setIdentifierColumn] = useState(ID_COL_AUTO);
  const [matchMode, setMatchMode] = useState(MATCH_EXACT);
  const [skipPrefix, setSkipPrefix] = useState("");
  const [alsoReconcile, setAlsoReconcile] = useState(false);
  const [includeArchived, setIncludeArchived] = useState(false);
  const lookupMode = lookupModeFor(identifierColumn);
  const [runTime, setRunTime] = useState(RUN_NOW);
  const [scheduledAt, setScheduledAt] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [downloadingJobId, setDownloadingJobId] = useState(null);
  const [cancellingJobId, setCancellingJobId] = useState(null);
  const [downloadingInputJobId, setDownloadingInputJobId] = useState(null);
  const [restartingJobId, setRestartingJobId] = useState(null);
  const { addToast } = useToast();
  const [showInfoModal, setShowInfoModal] = useState(false);

  const minScheduledAt = formatLocalDatetimeInput(new Date());

  const {
    jobs,
    page,
    totalPages,
    totalFinished,
    setPage,
    refetch: refetchJobs,
  } = useJobsPagination({
    endpoint: "/shopify/add-product-tag/jobs",
    cacheKey: "tagUpdate.jobs",
    pageSize: 10,
  });

  const toolInfo = {
    toolName: "Tag Update",
    description:
      "Bulk-add or remove a tag on Shopify products listed in an uploaded CSV or XLSX. Runs as a background job — you can leave this page while it runs.",
    steps: [
      "Upload a CSV or XLSX. Recognized identifier columns: SKU (also 'Variant SKU' / 'Parent SKU'), SHOPIFY_PRODUCT_ID, HANDLE, or 'Product Title' (also accepts 'Title', 'Name', or 'Product Name').",
      "Choose Add or Delete.",
      "Pick which column to look up by — Auto cascades SKU → ID → Handle, or force a specific column when the file has overlapping identifiers.",
      "Enter the tag (e.g. 'spring-2026').",
      "Click 'Start Job'. You'll get a job ID and live progress.",
      "Leave the page if you like — jobs run server-side and resume here when you come back.",
      "When the job finishes, download the report. It lists each row's status per the action.",
    ],
    templates: [],
  };

  const guideTips = [
    "Why async: Shopify's API is rate-limited (~2 calls/sec). A 100k-row file can take many hours, well beyond a normal HTTP timeout.",
    "Progress is tracked server-side in MongoDB, so you can close this tab and check back later.",
    "Identifier priority in Auto mode: SKU (parent) → SHOPIFY_PRODUCT_ID → HANDLE. The first non-blank one is used; misses cascade to the next. Pick a specific column to disable the cascade.",
    "Add action: rows where the tag is already present are skipped (counted under 'already_tagged'). Delete action: rows where the tag is not present are skipped (counted under 'not_present').",
    "The output workbook has a Summary sheet (totals) and a Results sheet (per-row status with the lookup used and matched product).",
  ];

  // Toast when an active job transitions to terminal during polling.
  const previousStatusRef = useRef({});
  useEffect(() => {
    const previous = previousStatusRef.current;
    jobs.forEach((j) => {
      const prevStatus = previous[j.job_id];
      if (prevStatus && prevStatus !== j.status) {
        if (j.status === STATUS_DONE) {
          addToast(`Tag job ${j.job_id} complete. Report is ready.`, "success");
        } else if (j.status === STATUS_FAILED) {
          addToast(
            `Tag job ${j.job_id} failed: ${j.error || "unknown error"}`,
            "error"
          );
        } else if (j.status === STATUS_CANCELLED) {
          addToast(`Tag job ${j.job_id} cancelled.`, "info");
        }
      }
    });
    previousStatusRef.current = Object.fromEntries(
      jobs.map((j) => [j.job_id, j.status])
    );
  }, [jobs, addToast]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!inputFile) {
      addToast("Please upload an input file.", "error");
      return;
    }

    const trimmedTag = tag.trim();
    if (!trimmedTag) {
      addToast("Please enter the tag to add.", "error");
      return;
    }

    const maxSize = 100 * 1024 * 1024;
    if (inputFile.size > maxSize) {
      addToast("File is too large. Maximum size is 100MB.", "error");
      return;
    }

    let scheduledIso = null;
    if (runTime === RUN_SCHEDULE) {
      if (!scheduledAt) {
        addToast("Pick a date and time to schedule the job.", "error");
        return;
      }
      const target = new Date(scheduledAt);
      if (Number.isNaN(target.getTime())) {
        addToast("Scheduled date/time is not valid.", "error");
        return;
      }
      if (target.getTime() <= Date.now() + 30 * 1000) {
        addToast("Scheduled date/time must be at least 30 seconds in the future.", "error");
        return;
      }
      scheduledIso = target.toISOString();
    }

    const formData = new FormData();
    formData.append("input_file", inputFile);
    const effectiveMatchMode = matchMode;
    const trimmedSkipPrefix =
      action === ACTION_ADD && effectiveMatchMode === MATCH_PREFIX
        ? skipPrefix.trim()
        : "";

    if (action === ACTION_ADD && effectiveMatchMode === MATCH_PREFIX && !trimmedSkipPrefix) {
      addToast("Please enter a skip prefix for add + prefix mode.", "error");
      return;
    }

    formData.append("tag", trimmedTag);
    formData.append("match_mode", effectiveMatchMode);
    if (trimmedSkipPrefix) {
      formData.append("skip_prefix", trimmedSkipPrefix);
    }
    if (identifierColumn) {
      formData.append("identifier_column", identifierColumn);
    }
    const trimmedSheet = sheetName.trim();
    if (trimmedSheet) {
      formData.append("sheet_name", trimmedSheet);
    }
    if (alsoReconcile) {
      formData.append("also_reconcile", "true");
    }
    formData.append("include_archived", includeArchived ? "true" : "false");
    if (scheduledIso) {
      formData.append("scheduled_at", scheduledIso);
    }

    setUploading(true);
    setUploadProgress(0);

    const endpoint =
      ENDPOINT_MATRIX[action]?.[lookupMode] ||
      ENDPOINT_MATRIX[ACTION_ADD][LOOKUP_IDENTIFIER];
    try {
      const response = await flaskApi.post(endpoint, formData, {
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / (progressEvent.total || 1)
          );
          setUploadProgress(percentCompleted);
        },
      });

      const jobId = response.data?.job_id;
      if (!jobId) {
        addToast("Server did not return a job ID.", "error");
        return;
      }
      if (scheduledIso) {
        addToast(
          `Tag job ${jobId} scheduled for ${formatScheduledDisplay(scheduledIso)}.`,
          "info"
        );
      } else {
        addToast(`Tag job ${jobId} queued. Tracking progress…`, "info");
      }
      // Snap back to page 1 so the new job is visible, then force-refresh.
      if (page !== 1) await setPage(1);
      else await refetchJobs();
      setInputFile(null);
      setScheduledAt("");
      setRunTime(RUN_NOW);
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to start tag job.");
      addToast(message, "error");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleCancel = async (job) => {
    if (!job?.job_id) return;
    const ok = window.confirm(
      `Cancel scheduled tag job ${job.job_id}? This can't be undone.`
    );
    if (!ok) return;
    setCancellingJobId(job.job_id);
    try {
      await flaskApi.delete(`/shopify/add-product-tag/jobs/${job.job_id}`);
      addToast(`Job ${job.job_id} cancelled.`, "success");
      await refetchJobs();
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to cancel job.");
      addToast(message, "error");
    } finally {
      setCancellingJobId(null);
    }
  };

  const handleDownload = async (job) => {
    if (!job?.job_id) return;
    setDownloadingJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `/shopify/add-product-tag/jobs/${job.job_id}/download`,
        { responseType: "blob" }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition =
        response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute(
        "download",
        extractedFilename || job.result_filename || "product_tag_report.xlsx"
      );
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      addToast("Report downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to download report.");
      addToast(message, "error");
    } finally {
      setDownloadingJobId(null);
    }
  };

  const handleDownloadInput = async (job) => {
    if (!job?.job_id) return;
    setDownloadingInputJobId(job.job_id);
    try {
      const response = await flaskApi.get(
        `/shopify/add-product-tag/jobs/${job.job_id}/input`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const contentDisposition =
        response.headers["content-disposition"] || response.headers["Content-Disposition"];
      const extractedFilename = extractFilenameFromHeader(contentDisposition);
      link.setAttribute("download", extractedFilename || job.input_filename || "input_file");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      addToast("Input file downloaded.", "success");
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to download input file.");
      addToast(message, "error");
    } finally {
      setDownloadingInputJobId(null);
    }
  };

  const handleRestart = async (job, scheduledAtLocal) => {
    if (!job?.job_id) return;
    let scheduledIso = null;
    if (scheduledAtLocal) {
      const target = new Date(scheduledAtLocal);
      if (Number.isNaN(target.getTime())) {
        addToast("Scheduled date/time is not valid.", "error");
        return;
      }
      if (target.getTime() <= Date.now() + 30 * 1000) {
        addToast("Scheduled date/time must be at least 30 seconds in the future.", "error");
        return;
      }
      scheduledIso = target.toISOString();
    }
    setRestartingJobId(job.job_id);
    try {
      await flaskApi.post(
        `/shopify/add-product-tag/jobs/${job.job_id}/restart`,
        scheduledIso ? { scheduled_at: scheduledIso } : {}
      );
      addToast(
        scheduledIso
          ? `Tag job ${job.job_id} rescheduled for ${formatScheduledDisplay(scheduledIso)}.`
          : `Tag job ${job.job_id} restarted.`,
        scheduledIso ? "info" : "success"
      );
      if (page !== 1) await setPage(1);
      else await refetchJobs();
    } catch (error) {
      const message = await extractErrorMessage(error, "Failed to restart job.");
      addToast(message, "error");
    } finally {
      setRestartingJobId(null);
    }
  };

  return (
    <>
      <ToolPageLayout
        splitLayout
        title="Tag Update"
        subtitle={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
        tips={guideTips}
        onOpenInfo={() => setShowInfoModal(true)}
      >
        <form onSubmit={handleSubmit} className="tool-form">
          <div className="tool-form-main">
            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Input File</h3>
              </div>
              <ToolFileUploadField
                label="Product list"
                file={inputFile}
                setFile={setInputFile}
                accept=".csv,.xlsx,.xls"
                helperText={
                  identifierColumn
                    ? `CSV or XLSX with a ${ID_COL_LABEL[identifierColumn].replace(/ column$/, "")} column.`
                    : "CSV or XLSX with at least one of: SKU (parent SKU), SHOPIFY_PRODUCT_ID, HANDLE."
                }
              />
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Tab Name <span className="tool-section-optional">(optional)</span></h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label">Excel tab to read</label>
                <p className="tool-helper-text">
                  Only used for .xlsx / .xls files with multiple tabs. Leave blank to use the first tab. Case-sensitive — must match the tab name exactly.
                </p>
                <input
                  type="text"
                  className="tool-input"
                  placeholder="e.g. Outlet Store"
                  value={sheetName}
                  onChange={(event) => setSheetName(event.target.value)}
                  disabled={uploading}
                />
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Action</h3>
              </div>
              <div className="tool-field-stack">
                <div className="tool-radio-group" role="radiogroup" aria-label="Tag action">
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="tag-action"
                      value={ACTION_ADD}
                      checked={action === ACTION_ADD}
                      onChange={() => setAction(ACTION_ADD)}
                      disabled={uploading}
                    />
                    <span>Add</span>
                  </label>
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="tag-action"
                      value={ACTION_REMOVE}
                      checked={action === ACTION_REMOVE}
                      onChange={() => setAction(ACTION_REMOVE)}
                      disabled={uploading}
                    />
                    <span>Delete</span>
                  </label>
                  <span className="tool-helper-text tool-radio-hint">
                    {ACTION_HELPER_TEXT[action]}
                  </span>
                </div>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Match Mode</h3>
              </div>
              <div className="tool-field-stack">
                <div className="tool-radio-group" role="radiogroup" aria-label="Match mode">
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="match-mode"
                      value={MATCH_EXACT}
                      checked={matchMode === MATCH_EXACT}
                      onChange={() => setMatchMode(MATCH_EXACT)}
                      disabled={uploading}
                    />
                    <span>Exact</span>
                  </label>
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="match-mode"
                      value={MATCH_PREFIX}
                      checked={matchMode === MATCH_PREFIX}
                      onChange={() => setMatchMode(MATCH_PREFIX)}
                      disabled={uploading}
                    />
                    <span>Prefix</span>
                  </label>
                  <span className="tool-helper-text tool-radio-hint">
                    {matchHelperText(action, matchMode)}
                  </span>
                </div>
              </div>
            </section>

            {action === ACTION_ADD && matchMode === MATCH_PREFIX ? (
              <section className="tool-section">
                <div className="tool-section-header">
                  <h3 className="tool-section-title">Skip Prefix</h3>
                </div>
                <div className="tool-field-stack">
                  <label className="tool-label">Skip if existing tag starts with</label>
                  <p className="tool-helper-text">
                    Used to detect that the product already has a tag from this family
                    (e.g. enter <code>offmap_</code> to skip if the product already has
                    <code> offmap_2026-05-21</code> or any other <code>offmap_…</code> tag).
                  </p>
                  <input
                    type="text"
                    className="tool-input"
                    placeholder="e.g. offmap_"
                    value={skipPrefix}
                    onChange={(event) => setSkipPrefix(event.target.value)}
                    disabled={uploading}
                    required
                  />
                </div>
              </section>
            ) : null}

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Lookup By</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label" htmlFor="identifier-column">
                  Match Shopify products via
                </label>
                <select
                  id="identifier-column"
                  className="tool-input"
                  value={identifierColumn}
                  onChange={(event) => setIdentifierColumn(event.target.value)}
                  disabled={uploading}
                >
                  {ID_COL_OPTIONS.map((opt) => (
                    <option key={opt.value || "auto"} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
                <span className="tool-helper-text">
                  {ID_COL_HELPER[identifierColumn]}
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Archived Products</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-checkbox-option">
                  <input
                    type="checkbox"
                    checked={includeArchived}
                    onChange={(event) => setIncludeArchived(event.target.checked)}
                    disabled={uploading}
                  />
                  <span>Also match archived products</span>
                </label>
                <span className="tool-helper-text">
                  By default only active products are matched (tagged/untagged). Tick this to also apply the tag change to archived products — matched archived products are flagged in the report. Draft products are always skipped.
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">Tag</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-label">
                  {action === ACTION_REMOVE && matchMode === MATCH_PREFIX
                    ? "Tag prefix to remove"
                    : action === ACTION_ADD && matchMode === MATCH_PREFIX
                    ? "Tag to add"
                    : action === ACTION_REMOVE
                    ? "Tag to remove"
                    : "Tag to add"}
                </label>
                <p className="tool-helper-text">
                  {action === ACTION_REMOVE && matchMode === MATCH_PREFIX
                    ? "Every tag on the matched product whose name starts with this value will be removed. Include the trailing separator (e.g. 'on_map_') to avoid matching unintended tags."
                    : action === ACTION_ADD && matchMode === MATCH_PREFIX
                    ? "The full tag value to write when the skip-prefix check above doesn't find an existing match. E.g. 'offmap_2026-05-21'."
                    : `The tag string to ${action === ACTION_REMOVE ? "remove from" : "apply to"} every matched product. ${ACTION_HELPER_TEXT[action]}`}
                </p>
                <input
                  type="text"
                  className="tool-input"
                  placeholder={
                    action === ACTION_REMOVE && matchMode === MATCH_PREFIX
                      ? "e.g. on_map_"
                      : action === ACTION_ADD && matchMode === MATCH_PREFIX
                      ? "e.g. offmap_2026-05-21"
                      : "e.g. spring-2026"
                  }
                  value={tag}
                  onChange={(event) => setTag(event.target.value)}
                  disabled={uploading}
                  required
                />
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">MAP Reconcile</h3>
              </div>
              <div className="tool-field-stack">
                <label className="tool-checkbox-option">
                  <input
                    type="checkbox"
                    checked={alsoReconcile}
                    onChange={(event) => setAlsoReconcile(event.target.checked)}
                    disabled={uploading}
                  />
                  <span>Also reconcile MAP status after tagging</span>
                </label>
                <span className="tool-helper-text">
                  When the tag job finishes, runs a MAP-status reconcile on the same file — keeps only each product's latest <code>on_map_</code>/<code>off_map_</code> tag and removes the rest. The reconcile runs as its own job; find its report in the <strong>MAP Status Reconcile</strong> tool. Requires an XLSX/XLS file (skipped for CSV).
                </span>
              </div>
            </section>

            <section className="tool-section">
              <div className="tool-section-header">
                <h3 className="tool-section-title">When to Run</h3>
              </div>
              <div className="tool-field-stack">
                <div
                  className="tool-radio-group"
                  role="radiogroup"
                  aria-label="When to run"
                >
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="tag-run-time"
                      value={RUN_NOW}
                      checked={runTime === RUN_NOW}
                      onChange={() => setRunTime(RUN_NOW)}
                      disabled={uploading}
                    />
                    <span>Run now</span>
                  </label>
                  <label className="tool-radio-option">
                    <input
                      type="radio"
                      name="tag-run-time"
                      value={RUN_SCHEDULE}
                      checked={runTime === RUN_SCHEDULE}
                      onChange={() => setRunTime(RUN_SCHEDULE)}
                      disabled={uploading}
                    />
                    <span>Schedule for later</span>
                  </label>
                </div>
                {runTime === RUN_SCHEDULE ? (
                  <>
                    <label className="tool-label" htmlFor="tag-scheduled-at">
                      Date &amp; time
                    </label>
                    <input
                      id="tag-scheduled-at"
                      type="datetime-local"
                      className="tool-input"
                      value={scheduledAt}
                      min={minScheduledAt}
                      onChange={(event) => setScheduledAt(event.target.value)}
                      disabled={uploading}
                      required
                    />
                    <span className="tool-helper-text">
                      Uses your browser's local time. The file is uploaded now and the job will fire at the scheduled time — you can close the page in between.
                    </span>
                  </>
                ) : null}
              </div>
            </section>

            <div className="tool-action-stack">
              <button
                type="submit"
                className="tool-btn-primary"
                disabled={uploading}
              >
                {uploading
                  ? "Uploading…"
                  : runTime === RUN_SCHEDULE
                  ? `Schedule ${action === ACTION_REMOVE ? "Delete" : "Add"} Job`
                  : `Start ${action === ACTION_REMOVE ? "Delete" : "Add"} Job`}
              </button>

              <ToolProgressPanel
                loading={uploading}
                uploadProgress={uploadProgress}
                uploadMessage="Uploading file…"
                processingMessage="Queueing job…"
              />
            </div>
          </div>
        </form>

        <ToolJobsSection
          title="Your Tag Jobs"
          emptyText="No tag jobs yet. Submit a file above to start one."
          jobs={jobs}
          page={page}
          totalPages={totalPages}
          totalFinished={totalFinished}
          onPageChange={setPage}
          renderJob={(j) => {
                const progress = j.progress || {};
                const total = Number(progress.total) || 0;
                const processed = Number(progress.processed) || 0;
                const pct =
                  total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
                const jobAction = j.action === ACTION_REMOVE ? ACTION_REMOVE : ACTION_ADD;
                const actionLabel = jobAction === ACTION_REMOVE ? "Delete" : "Add";
                const jobLookup = j.lookup_mode === LOOKUP_TITLE ? LOOKUP_TITLE : LOOKUP_IDENTIFIER;
                const jobMatch = j.match_mode === MATCH_PREFIX ? MATCH_PREFIX : MATCH_EXACT;
                return (
                  <div key={j.job_id} className="tool-job-status">
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Job ID</span>
                      <span className="tool-job-value">{j.job_id}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Action</span>
                      <span className="tool-job-value">{actionLabel}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Lookup</span>
                      <span className="tool-job-value">
                        {j.identifier_column
                          ? ID_COL_LABEL[j.identifier_column] || j.identifier_column
                          : LOOKUP_LABEL[jobLookup]}
                      </span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Match</span>
                      <span className="tool-job-value">{MATCH_LABEL[jobMatch]}</span>
                    </div>
                    {j.skip_prefix ? (
                      <div className="tool-job-status-row">
                        <span className="tool-job-label">Skip Prefix</span>
                        <span className="tool-job-value">{j.skip_prefix}</span>
                      </div>
                    ) : null}
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Tag</span>
                      <span className="tool-job-value">{j.tag || "—"}</span>
                    </div>
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Input</span>
                      <span className="tool-job-value">{j.input_filename || "—"}</span>
                    </div>
                    {j.sheet_name ? (
                      <div className="tool-job-status-row">
                        <span className="tool-job-label">Tab</span>
                        <span className="tool-job-value">{j.sheet_name}</span>
                      </div>
                    ) : null}
                    {j.include_archived ? (
                      <div className="tool-job-status-row">
                        <span className="tool-job-label">Archived</span>
                        <span className="tool-job-value">Included</span>
                      </div>
                    ) : null}
                    {j.also_reconcile ? (
                      <div className="tool-job-status-row">
                        <span className="tool-job-label">MAP Reconcile</span>
                        <span className="tool-job-value">
                          {progress.reconcile_job_id
                            ? `Queued (job ${progress.reconcile_job_id}) — see MAP Status Reconcile`
                            : progress.reconcile_skipped
                            ? `Skipped — ${progress.reconcile_skipped}`
                            : "Enabled"}
                        </span>
                      </div>
                    ) : null}
                    {j.scheduled_at ? (
                      <div className="tool-job-status-row">
                        <span className="tool-job-label">Scheduled</span>
                        <span className="tool-job-value">
                          {formatScheduledDisplay(j.scheduled_at)}
                        </span>
                      </div>
                    ) : null}
                    <div className="tool-job-status-row">
                      <span className="tool-job-label">Status</span>
                      <span className={`tool-job-value tool-job-status-${j.status}`}>
                        {j.status}
                      </span>
                    </div>
                    {total > 0 ? (
                      <>
                        <div className="tool-job-status-row">
                          <span className="tool-job-label">Progress</span>
                          <span className="tool-job-value">
                            {processed.toLocaleString()} / {total.toLocaleString()} ({pct}%)
                          </span>
                        </div>
                        <div className="progress-bar-container">
                          <div className="progress-bar-fill" style={{ width: `${pct}%` }} />
                        </div>
                      </>
                    ) : null}
                    <div className="tool-job-counts">
                      {jobAction === ACTION_REMOVE ? (
                        <>
                          <span>Untagged: {progress.untagged || 0}</span>
                          <span>Not present: {progress.not_present || 0}</span>
                        </>
                      ) : (
                        <>
                          <span>Tagged: {progress.tagged || 0}</span>
                          <span>Already tagged: {progress.already_tagged || 0}</span>
                        </>
                      )}
                      <span>Not found: {progress.not_found || 0}</span>
                      <span>Errors: {progress.errors || 0}</span>
                      <span>Skipped: {progress.skipped || 0}</span>
                    </div>
                    {j.status === STATUS_FAILED && j.error ? (
                      <div className="tool-job-error">Error: {j.error}</div>
                    ) : null}
                    {j.status === STATUS_DONE ? (
                      <button
                        type="button"
                        className="tool-btn-primary"
                        onClick={() => handleDownload(j)}
                        disabled={downloadingJobId === j.job_id}
                      >
                        {downloadingJobId === j.job_id ? "Downloading…" : "Download Report"}
                      </button>
                    ) : null}
                    {j.status === STATUS_SCHEDULED ? (
                      <button
                        type="button"
                        className="tool-btn-secondary"
                        onClick={() => handleCancel(j)}
                        disabled={cancellingJobId === j.job_id}
                      >
                        {cancellingJobId === j.job_id ? "Cancelling…" : "Cancel"}
                      </button>
                    ) : null}
                    <JobInputActions
                      job={j}
                      onDownloadInput={handleDownloadInput}
                      downloadingInput={downloadingInputJobId === j.job_id}
                      onRestart={handleRestart}
                      restarting={restartingJobId === j.job_id}
                      minScheduledAt={minScheduledAt}
                    />
                  </div>
                );
              }}
            />
      </ToolPageLayout>

      <ToolInfoModal
        isOpen={showInfoModal}
        onClose={() => setShowInfoModal(false)}
        toolName={toolInfo.toolName}
        description={toolInfo.description}
        steps={toolInfo.steps}
        templates={toolInfo.templates}
      />
    </>
  );
}


export default TagUpdateTool;
